import { isEqual } from 'lodash';
import queryString from 'querystring';
import React, { Component } from 'react';
import { getClass } from '../../../../utils/component-helpers';

import analyticService from '../../../../services/analytics';
import { session } from '../../../../services/session';
import { Loader, NoResult } from '../../../misc/loader-utils';
import productService from '../product-service';

import CartSiblings from './cart-siblings';
import Pagination from '../../../base/common/pagination';
import Product from './product';
import SortDropdown from '../../../base/dropdowns/sort-dropdown';

class CartExplorer extends Component {
  constructor(props) {
    super(props);

    this.AnalyticService = analyticService;
    this.service = productService;
    this.Session = session;

    this.logoMap = {};
    this.sortOptions = [
      {
        label: 'Purchased',
        name: 'total_quantity',
        direction: 'desc',
        selected: true,
        identifier: 'quantity-desc',
      },
      /*
       * Price is usually feed_price_usd, but default to unit_price_usd
       * if feed price isn't available.
       */
      {
        label: 'Price - high to low',
        name: 'sale_price',
        direction: 'desc',
        selected: false,
        identifier: 'price-desc',
      },
      {
        label: 'Price - low to high',
        name: 'sale_price',
        direction: 'asc',
        selected: false,
        identifier: 'price-asc',
      },
      {
        label: 'Discounts',
        name: 'sale_percent',
        direction: 'desc',
        selected: false,
        identifier: 'sale-desc',
      },
      {
        label: 'Date Added',
        name: 'datetime_created',
        direction: 'desc',
        selected: false,
        identifier: 'datetime-desc',
      },
    ];
    this.defaultSort = 'total_quantity';
    this.tabOptions = [
      { label: 'Best Sellers', name: 'all' },
      { label: 'Items on Sale', name: 'sale' },
      { label: 'New Products', name: 'new' },
    ];
    this.headerText = {
      all: 'What are the best sellers?',
      new: 'What are new products?',
      sale: 'What items are on sale that have generated the biggest carts?',
    };
    this.table = null;

    this.state = {
      currentDateRange: this.props.currentDateRange,
      products: {
        '30d': [],
        '24h': [],
      },
      selectedProductIdx: 0, // Default to selecting first product in table
      sortOptions: this.sortOptions,
      currentTab: 'all',

      // Pagination
      page: 1,
      perPage: this.service.paginationOptions[2],
      totalItems: 0,

      loading: true,
      showNoResult: false,
    };
  }

  componentDidMount() {
    const updatedState = this.getStateFromQueryString();
    this.props.merchants.forEach(merchant => {
      this.logoMap[merchant.merch_id] = merchant.logo_url;
    });

    this.setState(updatedState, () => {
      // setQueryString() fills out default values if they aren't set
      this.setQueryString();

      this.queryCartSummary();
      this.queryBestSellers();
    });
  }

  componentDidUpdate(prevProps, prevState) {
    const prevMerchIds = queryString.parse(prevProps.urlQuery).merch_ids;
    const currentMerchIds = queryString.parse(this.props.urlQuery).merch_ids;
    const searchChange = prevProps.searchTerm !== this.props.searchTerm;

    if (
      searchChange ||
      prevState.currentTab !== this.state.currentTab ||
      !isEqual(prevMerchIds, currentMerchIds) ||
      !isEqual(prevState.sortOptions, this.state.sortOptions)
    ) {
      // Reset page when user selects a new merchant, search, or sort
      this.setState(
        {
          page: 1,
          selectedProductIdx: 0,
          loading: true, // Set loading here to prevent cart component from unmounting
        },
        () => {
          this.setQueryString({ page: 1 });
          this.queryBestSellers(true).then(() => {
            // Reset scroll when merchant changes
            this.table.scrollTop = 0;
          });

          // Only re-query totals on search
          if (searchChange) this.queryCartSummary();
        },
      );
    }

    /*
     * Display loader when user selects a new merchant via carousel
     * to prevent showing debounce delay to clients
     */
    if (!isEqual(prevProps.merchants, this.props.merchants)) {
      this.setState({ loading: true });
    }

    if (!isEqual(prevProps.currentDateRange, this.props.currentDateRange)) {
      this.setState(
        {
          currentDateRange: this.props.currentDateRange,
          loading: true,
        },
        () => {
          this.setQueryString();
          this.queryCartSummary();
          this.queryBestSellers();
        },
      );
    }
  }

  /* Params handling */
  getStateFromQueryString() {
    // Preserve table state by getting params from the query string
    const searchParams = new URLSearchParams(this.props.location.search);

    let sortAvailable = false;
    const sortOptions = this.sortOptions.slice();
    sortOptions.forEach(option => {
      if (
        option.name === searchParams.get('order_by') &&
        option.direction === searchParams.get('direction')
      ) {
        option.selected = true;
        sortAvailable = true;
      } else {
        option.selected = false;
      }
    });
    if (!sortAvailable) sortOptions[0].selected = true;

    return {
      page: parseInt(searchParams.get('page')) || this.state.page,
      perPage: parseInt(searchParams.get('per_page')) || this.state.perPage,
      sortOptions,
      currentTab: searchParams.get('tab') || this.state.currentTab,
    };
  }

  setQueryString() {
    /*
     * CartExplorer and SearchTrends are responsible for adding
     * to the query string params that are important to them,
     * using a helper function from Insights.
     */
    const params = this.getRequestParams();
    Object.assign(params, {
      page: this.state.page,
      per_page: this.state.perPage,
      tab: this.state.currentTab,
    });

    this.props.setQueryString(params);
  }

  queryCartSummary() {
    this.props.updateProductData({});

    const params = this.getRequestParams(true);
    return this.service.queryCartSummary(this.props.pubId, params).then(totals => {
      this.props.updateProductData(totals);
    });
  }

  queryBestSellers(resetPages = false) {
    this.setState({
      loading: true,
      totalItems: 0,
    });

    const params = this.getRequestParams();
    const { merchIds } = this.props;
    const dateRange = this.state.currentDateRange;
    return this.service
      .getProductTrends(
        this.props.pubId,
        merchIds,
        params,
        this.state.page,
        this.state.perPage,
        dateRange,
        resetPages,
      )
      .then(products => {
        if (!products.length) {
          this.setState({ showNoResult: true, loading: false });
          return;
        }

        const totalItems = this.service.productsByMerch[merchIds][dateRange].total_items;

        const bestSellerProducts = { ...this.state.products };
        bestSellerProducts[dateRange] = products;

        this.setState({
          products: bestSellerProducts,
          loading: false,
          showNoResult: false,
          totalItems,
        });

        /*
         * Call to fetch data for other date range so it will preload
         * No longer pre-fetch page 2, as preloading with 2 queries causes async issues
         */
        const otherDateRange = dateRange === '30d' ? '24h' : '30d';
        this.service.getProductTrends(
          this.props.pubId,
          merchIds,
          params,
          this.state.page,
          this.state.perPage,
          otherDateRange,
          true,
        );
      });
  }

  getRequestParams(isTotal = false) {
    // Return query params for the best seller request
    const params = {
      date_from: this.props.params.date_from,
      date_to: this.props.params.date_to,
      merch_ids: this.props.merchIds,
      search_term: this.props.searchTerm || null,
    };

    if (this.state.currentTab !== 'all') {
      params.product_type = this.state.currentTab;
    }

    if (isTotal) {
      // Always query cart summary for all merchants
      const merchIds = this.props.merchants
        .filter(merch => !merch.hidden)
        .map(merch => merch.merch_id)
        .join(',');
      Object.assign(params, { merch_ids: merchIds });
    } else {
      const sortOption = this.state.sortOptions.filter(option => option.selected)[0];
      Object.assign(params, {
        order_by: sortOption.name,
        direction: sortOption.direction,
      });
    }

    return params;
  }

  /* Actions */
  updateState(name) {
    // Used to update page, perPage, sortOptions
    return value => {
      this.setState(
        {
          [name]: value,
          selectedProductIdx: 0,
        },
        () => {
          this.setQueryString();
          this.queryBestSellers().then(() => {
            this.table.scrollTop = 0;
          });
        },
      );
    };
  }

  selectProduct(index) {
    const productName = this.state.products[this.state.currentDateRange][index].product_name;
    this.AnalyticService.trackEvent('Cart Explorer', 'Click', `Best Seller Product ${productName}`);

    this.setState({ selectedProductIdx: index });
  }

  trackLinkOut(url) {
    const gaLabel = `Hyperlink on Best Seller Product ${url}`;
    this.AnalyticService.trackEvent('Cart Explorer', 'Click', gaLabel);
  }

  changeTabs(tabOption) {
    this.AnalyticService.trackEvent('Cart Explorer', 'Click', `${tabOption.label} tab`);

    const newSortOptions = this.state.sortOptions.map(sortOption => {
      return { ...sortOption, selected: sortOption.name === this.defaultSort };
    });
    this.setState({
      currentTab: tabOption.name,
      sortOptions: newSortOptions,
    });
  }

  render() {
    const scrollable = this.props.scrollable ? 'scrollable' : '';
    const selectedProduct = this.state.selectedProductIdx;

    const products =
      !this.state.loading && !this.state.showNoResult
        ? this.state.products[this.state.currentDateRange].map((product, i) => {
            const merchant = this.props.merchants
              .filter(merch => merch.selected)
              .find(merch => {
                return merch.merch_id === product.merch_id;
              });
            return (
              <Product
                hiddenBorder={selectedProduct - 1 === i}
                key={i}
                merchant={merchant}
                product={product}
                selected={selectedProduct === i}
                selectProduct={() => this.selectProduct(i)}
                trackLinkOut={url => this.trackLinkOut(url)}
                currentTab={this.state.currentTab}
              />
            );
          })
        : null;
    const cart =
      !this.state.loading && !this.state.showNoResult ? (
        <CartSiblings
          logoMap={this.logoMap}
          params={this.props.params}
          product={this.state.products[this.state.currentDateRange][selectedProduct]}
          pubId={this.props.pubId}
          scrollable={scrollable}
          trackLinkOut={url => this.trackLinkOut(url)}
          currentDateRange={this.state.currentDateRange}
        />
      ) : null;
    const tabOptions = this.tabOptions.map(option => {
      return (
        <div
          key={option.name}
          className={getClass(`tab-option ${option.name}`, {
            selected: this.state.currentTab === option.name,
          })}
          onClick={() => this.changeTabs(option)}
        >
          {option.label}
        </div>
      );
    });

    return (
      <div className="cart-explorer">
        <div className="tabs">{tabOptions}</div>
        <div className="cart-container">
          <div className={`best-sellers ${this.state.currentTab}`}>
            <div className="header">
              <h2>{this.headerText[this.state.currentTab]}</h2>
              <div className="dropdown-container">
                <span className="text">Sort By</span>
                <SortDropdown
                  gaEvent={{
                    category: 'Cart Explorer',
                    label: 'Sort By',
                  }}
                  items={this.state.sortOptions}
                  updateSortOptions={sortOptions =>
                    this.setState({
                      sortOptions,
                    })
                  }
                />
              </div>
            </div>
            <div ref={node => (this.table = node)} className={`best-sellers-table ${scrollable}`}>
              <Loader loading={this.state.loading} />
              <NoResult
                type="product-trends"
                noResult={!this.state.loading && this.state.showNoResult}
              />
              {products}
            </div>
            <div className="footer">
              <Pagination
                showPagination={!this.state.showNoResult}
                currentPage={this.state.page}
                totalItems={this.state.totalItems}
                perPage={this.state.perPage}
                eventPrefix="Cart Explorer"
                updatePage={this.updateState('page')}
                updatePerPage={this.updateState('perPage')}
              />
            </div>
          </div>
          {cart}
        </div>
      </div>
    );
  }
}

export default CartExplorer;
